import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  HelpCircle, 
  Lightbulb, 
  Info, 
  AlertCircle,
  CheckCircle,
  X,
  ArrowRight,
  BookOpen,
  Target,
  Trophy,
  Users,
  User,
  CalendarDays,
  BarChart2,
  Settings,
  Zap,
  Star,
  MessageSquare
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ContextualTip {
  id: string;
  title: string;
  description: string;
  category: 'info' | 'tip' | 'warning' | 'success';
  icon: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismissible?: boolean;
}

interface PageTips {
  [key: string]: ContextualTip[];
}

const ContextualHelp: React.FC = () => {
  const [showTips, setShowTips] = useState(false);
  const [dismissedTips, setDismissedTips] = useState<string[]>([]);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const location = useLocation();
  const { user } = useAuth();

  // Load dismissed tips from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dismissed-tips');
    if (saved) {
      setDismissedTips(JSON.parse(saved));
    }
  }, []);

  // Save dismissed tips to localStorage
  const dismissTip = (tipId: string) => {
    const newDismissed = [...dismissedTips, tipId];
    setDismissedTips(newDismissed);
    localStorage.setItem('dismissed-tips', JSON.stringify(newDismissed));
  };

  const pageTips: PageTips = {
    '/': [
      {
        id: 'welcome-tip',
        title: 'Welcome to Cricket Scoring System',
        description: 'Get started by creating your first tournament or explore the interactive help system.',
        category: 'info',
        icon: <Info className="h-4 w-4" />,
        action: {
          label: 'Get Started',
          onClick: () => window.location.href = '/tournaments/new'
        }
      },
      {
        id: 'help-tour',
        title: 'Interactive Tour Available',
        description: 'Take a guided tour to learn about all the features of the system.',
        category: 'tip',
        icon: <BookOpen className="h-4 w-4" />,
        action: {
          label: 'Start Tour',
          onClick: () => setShowTips(true)
        }
      }
    ],
    '/tournaments': [
      {
        id: 'tournament-bulk-import',
        title: 'Bulk Import Available',
        description: 'Import multiple tournaments at once using CSV files. Download the template to get started.',
        category: 'tip',
        icon: <Zap className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'tournament-organization',
        title: 'Organize Your Events',
        description: 'Create separate tournaments for different leagues, seasons, or age groups.',
        category: 'info',
        icon: <Trophy className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/tournaments/new': [
      {
        id: 'tournament-naming',
        title: 'Tournament Naming Tips',
        description: 'Use descriptive names like "Summer League 2024" or "Corporate Cup Tournament".',
        category: 'tip',
        icon: <Lightbulb className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'tournament-scheduling',
        title: 'Plan Your Schedule',
        description: 'Set realistic start dates and consider weather conditions for outdoor tournaments.',
        category: 'info',
        icon: <CalendarDays className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/teams': [
      {
        id: 'team-colors',
        title: 'Team Colors',
        description: 'Assign unique colors to teams for easy identification in match schedules and statistics.',
        category: 'tip',
        icon: <Star className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'team-captains',
        title: 'Team Captains',
        description: 'Designate team captains who can help manage player rosters and team information.',
        category: 'info',
        icon: <User className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/teams/new': [
      {
        id: 'team-shortcode',
        title: 'Team Shortcodes',
        description: 'Use short, memorable codes like "MI" for Mumbai Indians or "CSK" for Chennai Super Kings.',
        category: 'tip',
        icon: <Lightbulb className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/players': [
      {
        id: 'player-roles',
        title: 'Player Roles',
        description: 'Specify player roles (Batsman, Bowler, All-rounder, Wicket-keeper) for better statistics.',
        category: 'tip',
        icon: <Target className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'player-contact',
        title: 'Player Contact Info',
        description: 'Add contact information for team communication and emergency situations.',
        category: 'info',
        icon: <User className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/players/new': [
      {
        id: 'player-details',
        title: 'Complete Player Profiles',
        description: 'Include player photos, contact details, and playing preferences for better team management.',
        category: 'tip',
        icon: <Lightbulb className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/matches': [
      {
        id: 'match-scheduling',
        title: 'Match Scheduling',
        description: 'Schedule matches with adequate time between games and consider city availability.',
        category: 'tip',
        icon: <CalendarDays className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'match-format',
        title: 'Match Formats',
        description: 'Choose appropriate formats (T20, ODI, Test) based on your tournament requirements.',
        category: 'info',
        icon: <Target className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/matches/new': [
      {
        id: 'match-city',
        title: 'City Information',
        description: 'Specify city details including ground conditions and facilities available.',
        category: 'tip',
        icon: <Lightbulb className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'match-officials',
        title: 'Match Officials',
        description: 'Assign umpires and match referees for professional tournament management.',
        category: 'info',
        icon: <User className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/statistics': [
      {
        id: 'stats-filters',
        title: 'Use Filters',
        description: 'Filter statistics by date range, tournament, team, or player for detailed analysis.',
        category: 'tip',
        icon: <BarChart2 className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'stats-export',
        title: 'Export Statistics',
        description: 'Export statistics in various formats for external analysis or reporting.',
        category: 'info',
        icon: <Zap className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/feedback': [
      {
        id: 'feedback-detailed',
        title: 'Detailed Feedback',
        description: 'Provide specific details and screenshots when reporting issues for faster resolution.',
        category: 'tip',
        icon: <MessageSquare className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'feedback-categories',
        title: 'Use Categories',
        description: 'Categorize your feedback to help us prioritize and address issues effectively.',
        category: 'info',
        icon: <Info className="h-4 w-4" />,
        dismissible: true
      }
    ],
    '/settings': [
      {
        id: 'settings-backup',
        title: 'Data Backup',
        description: 'Regularly export your data to ensure you don\'t lose important tournament information.',
        category: 'warning',
        icon: <AlertCircle className="h-4 w-4" />,
        dismissible: true
      },
      {
        id: 'settings-theme',
        title: 'Theme Preferences',
        description: 'Choose between light and dark themes based on your environment and preference.',
        category: 'tip',
        icon: <Settings className="h-4 w-4" />,
        dismissible: true
      }
    ]
  };

  const getCurrentPageTips = (): ContextualTip[] => {
    const tips = pageTips[location.pathname] || [];
    return tips.filter(tip => !dismissedTips.includes(tip.id));
  };

  const currentTips = getCurrentPageTips();
  const hasTips = currentTips.length > 0;

  const getTipCategoryStyles = (category: string) => {
    switch (category) {
      case 'info':
        return 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'tip':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
      case 'warning':
        return 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 'success':
        return 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/50 dark:text-green-300';
      default:
        return 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300';
    }
  };

  const nextTip = () => {
    if (currentTipIndex < currentTips.length - 1) {
      setCurrentTipIndex(currentTipIndex + 1);
    } else {
      setCurrentTipIndex(0);
    }
  };

  const prevTip = () => {
    if (currentTipIndex > 0) {
      setCurrentTipIndex(currentTipIndex - 1);
    } else {
      setCurrentTipIndex(currentTips.length - 1);
    }
  };

  if (!hasTips) return null;

  const currentTip = currentTips[currentTipIndex];

  return (
    <TooltipProvider>
      <div className="fixed bottom-4 right-4 z-40">
        {/* Help Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTips(!showTips)}
              className="rounded-full w-12 h-12 shadow-lg bg-background border-2"
            >
              <HelpCircle className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p>Contextual Help & Tips</p>
          </TooltipContent>
        </Tooltip>

        {/* Tips Panel */}
        {showTips && (
          <Card className="w-80 mt-2 shadow-xl border-2 bg-background">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
                  <CardTitle className="text-sm">Helpful Tips</CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="secondary" className="text-xs">
                    {currentTipIndex + 1} of {currentTips.length}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTips(false)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className={`p-3 rounded-lg border ${getTipCategoryStyles(currentTip.category)}`}>
                <div className="flex items-start gap-2">
                  {currentTip.icon}
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{currentTip.title}</h4>
                    <p className="text-xs opacity-90">{currentTip.description}</p>
                  </div>
                </div>
                
                {currentTip.action && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={currentTip.action.onClick}
                    className="mt-3 w-full text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {currentTip.action.label}
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between mt-3">
                <div className="flex gap-1">
                  {currentTips.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentTipIndex ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
                
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={prevTip}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowRight className="h-3 w-3 rotate-180" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={nextTip}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {currentTip.dismissible && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissTip(currentTip.id)}
                  className="w-full mt-2 text-xs"
                >
                  Don't show again
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ContextualHelp; 